import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bell, Building2, LogOut, Palette, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ThemePicker } from "@/components/theme-picker";
import { useAuth } from "@/context/auth-context";
import { useOptionalTenant } from "@/context/tenant-context";
import { roleLabel } from "@/lib/role-label";
import {
  useNotificationPrefs,
  useSetChatToastsEnabled,
} from "@/hooks/use-notification-prefs";

/** One setting: name and a short line on the left, its control on the right. */
function Row({ label, description, children }: { label: ReactNode; description?: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-6">
      <div className="min-w-0">
        <p className="text-sm font-medium text-text-primary">{label}</p>
        {description && <p className="mt-0.5 text-xs text-text-muted">{description}</p>}
      </div>
      {children && <div className="shrink-0">{children}</div>}
    </div>
  );
}

/**
 * My account. Each setting is a row: its name and a short line on the left,
 * its control on the right (2026-10-02). Profile and Access were two cards
 * saying who you are; they are one card now, with the role that applies here:
 * MSP admin for Aztech staff, otherwise the person's role in this company.
 * The pop-up used to show only the platform role, so a tenant admin read
 * "Standard user".
 */
export function AccountSettingsContent({
  showBack = true,
  showTitle = true,
  compactAccess = false,
}: {
  showBack?: boolean;
  showTitle?: boolean;
  /** In the settings pop-up the company is already on screen, so skip it. */
  compactAccess?: boolean;
}) {
  const { user, signOut, isInstanceAdmin } = useAuth();
  const navigate = useNavigate();
  const tenant = useOptionalTenant();
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  const { data: notifPrefs, isLoading: notifLoading } = useNotificationPrefs();
  const setChatToasts = useSetChatToastsEnabled();
  const chatToastsEnabled = notifPrefs?.chatToastsEnabled !== false;

  const initials = (user?.name || user?.email || "U")
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const role = isInstanceAdmin
    ? "MSP admin"
    : tenant?.userRole
      ? roleLabel(tenant.userRole)
      : "Standard user";
  const roleTone =
    isInstanceAdmin || tenant?.userRole === "tenant_admin"
      ? "success"
      : tenant?.userRole === "agent_admin"
        ? "warning"
        : "secondary";

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  return (
    <>
      {(showTitle || showBack) && (
        <div className="mb-6 flex items-center justify-between">
          {showTitle ? (
            <h1 className="text-2xl font-semibold text-text-primary">My account</h1>
          ) : (
            <span />
          )}
          {showBack && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (isInstanceAdmin) {
                  navigate("/dashboard");
                } else if (tenant?.tenantId) {
                  navigate(`/tenants/${tenant.tenantId}/workspace`);
                } else {
                  navigate(-1);
                }
              }}
            >
              <ArrowLeft size={14} className="mr-1.5" />
              Back
            </Button>
          )}
        </div>
      )}

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <User size={18} />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-6">
            <div className="flex min-w-0 items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-accent text-lg font-semibold text-white">
                {initials}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-text-primary">{user?.name || "-"}</p>
                <p className="truncate text-xs text-text-muted">{user?.email}</p>
              </div>
            </div>
            <Badge variant={roleTone}>{role}</Badge>
          </div>
          {!compactAccess && tenant?.tenantName && (
            <Row label="Company" description="The workspace you're signed in to.">
              <span className="flex items-center gap-2 text-sm text-text-primary">
                <Building2 size={14} className="text-text-muted" />
                {tenant.tenantName}
              </span>
            </Row>
          )}
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Palette size={18} />
            Appearance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Row label="Theme" description="Light or dark, and the colours, on this device.">
            <Button size="sm" variant="outline" onClick={() => setThemePickerOpen(true)}>
              <Palette size={14} className="mr-1.5" />
              Change theme
            </Button>
          </Row>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell size={18} />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Row
            label="Agent reply alerts"
            description="Pop-up toasts when an agent posts a new message. We never show one for the chat you're currently viewing."
          >
            <Switch
              aria-label="Toggle agent reply alerts"
              checked={chatToastsEnabled}
              disabled={notifLoading || setChatToasts.isPending}
              onCheckedChange={(next) => setChatToasts.mutate(next)}
            />
          </Row>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="py-4">
          <Row label="Sign out" description="Sign out of ProClaw on this device.">
            <Button size="sm" variant="outline" onClick={() => setConfirmSignOut(true)}>
              <LogOut size={14} className="mr-1.5" />
              Sign out
            </Button>
          </Row>
        </CardContent>
      </Card>

      {themePickerOpen && (
        <ThemePicker onClose={() => setThemePickerOpen(false)} />
      )}

      {/* The same confirm as Sign out in the sidebar's user menu. */}
      {confirmSignOut && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setConfirmSignOut(false)}
        >
          <div
            className="w-[min(360px,calc(100vw-2rem))] rounded-lg border border-border bg-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-sm font-semibold text-text-primary">Sign out?</h3>
            <p className="mb-4 text-xs text-text-secondary">
              You'll need to sign in again to get back to ProClaw.
            </p>
            <div className="flex justify-end gap-2">
              {/* Focus starts on Cancel, so pressing Enter keeps you signed in. */}
              <Button size="sm" variant="ghost" autoFocus onClick={() => setConfirmSignOut(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setConfirmSignOut(false);
                  void handleSignOut();
                }}
              >
                Sign out
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function Account() {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-5xl px-8 py-6">
        <AccountSettingsContent />
      </div>
    </div>
  );
}
